import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@trillion/ui/components/ui/badge";
import { createActionsColumn } from "@trillion/ui/components/admin/actions-column";
import { SortableHeader } from "@trillion/ui/components/admin/sortable-header";
import { sortableInstant } from "@trillion/ui/lib/data-grid-sort";
import { Lock } from "lucide-react";
import type { SettingView } from "@/lib/settings-types";

function fmt(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

interface SettingsColumnActions {
  onEdit: (s: SettingView) => void;
  onDelete: (s: SettingView) => void;
  /** One-way plain → secret (sc-116657). Required, so every settings grid wires it. */
  onConvertToSecret: (s: SettingView) => void;
}

/** The row menu's one-way conversion; greyed out (never hidden) on a row that is already secret. */
const CONVERT_TO_SECRET_LABEL = "Convert to secret";

export function getSettingsColumns(actions: SettingsColumnActions): ColumnDef<SettingView>[] {
  return [
    {
      accessorKey: "scope",
      header: ({ column }) => <SortableHeader column={column} label="Scope" />,
      sortingFn: "text",
      cell: ({ row }) => <Badge variant="outline">{row.original.scope}</Badge>,
    },
    {
      accessorKey: "key",
      header: ({ column }) => <SortableHeader column={column} label="Key" />,
      sortingFn: "text",
      cell: ({ row }) => <span className="font-medium">{row.original.key}</span>,
    },
    {
      accessorKey: "value",
      header: ({ column }) => <SortableHeader column={column} label="Value" />,
      // Secrets come back `value: null`, so a value sort would order rows by data the operator cannot
      // see. Per the flag contract this is the one-word opt-out — and the shared header renders a plain
      // label for it with no per-page branching.
      enableSorting: false,
      cell: ({ row }) => {
        const s = row.original;
        // Secrets are NEVER rendered — the API masks them (value:null); show a set-indicator instead.
        if (s.is_secret) {
          return (
            <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
              <Lock className="size-3" />
              •••• set
            </span>
          );
        }
        return s.value ? (
          <code
            className="inline-block max-w-[24rem] truncate rounded bg-muted px-1.5 py-0.5 align-bottom text-xs"
            title={s.value}
          >
            {s.value}
          </code>
        ) : (
          <span className="text-sm text-muted-foreground">—</span>
        );
      },
    },
    {
      accessorKey: "is_secret",
      header: ({ column }) => <SortableHeader column={column} label="Secret" />,
      sortingFn: "basic",
      cell: ({ row }) =>
        row.original.is_secret ? (
          <Badge variant="secondary">Yes</Badge>
        ) : (
          <span className="text-sm text-muted-foreground">No</span>
        ),
    },
    {
      id: "modified_date",
      accessorFn: (s) => sortableInstant(s.modified_date),
      header: ({ column }) => <SortableHeader column={column} label="Modified" />,
      sortUndefined: "last",
      sortingFn: "basic",
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">{fmt(row.original.modified_date)}</span>
      ),
    },
    createActionsColumn<SettingView>({
      onEdit: actions.onEdit,
      onDelete: actions.onDelete,
      items: [
        {
          label: CONVERT_TO_SECRET_LABEL,
          icon: Lock,
          onSelect: actions.onConvertToSecret,
          disabled: (s) => s.is_secret,
        },
      ],
    }),
  ];
}
